/* eslint-disable @ngrx/no-store-subscription */
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { RouterModule, RouterOutlet } from '@angular/router';
import { LoginComponent } from './core/auth/login/login.component';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import * as dxLocale from 'devextreme/localization';
import config from 'devextreme/core/config';
import { licenseKey } from './core/licenses/devextreme-key';
import { SysSecuredMenu } from './core/models/sys-secured-menu.model';
import { LocalStorageService } from './core/services/local-storage.service';
import { Identifier } from './core/models/storage.model';
import { AuthService } from './core/services/auth.service';
import { filter, Subscription } from 'rxjs';
import { UserIdleService } from 'angular-user-idle';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Store } from '@ngrx/store';
import { selectPortalMode, selectUserIdle, setPortalMode, setUserIdle } from './store/ui';
import { SysExternalWebUserType } from './shared/enums/sys-application.enum';
import { SharedDynamicModule } from './shared/dynamic/dynamic.module';
import { HttpLoadingIndicatorService } from './core/services/http-loading-indicator.service';
import { ActionsService } from './core/services/actions.service';
import { PortalHttpService } from './core/services/portal-http.service';
import { setDefaultDevextremeOptions } from './core/utils/devextreme.utils';
import { GridUtilsModule } from './core/modules/grid-utils.module';
import { User } from './core/models/user.model';
import { ExternalPortalConfig } from './core/models/external-portal-config.model';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';

config({ licenseKey });

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    RouterOutlet,
    RouterModule,
    LoginComponent,
    CommonModule,
    TranslateModule,
    SharedDynamicModule,
    GridUtilsModule,
  ],
  templateUrl: './app.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit {
  title = 'SSICS-External';
  portalUser?: User;

  isLoggedIn = false;
  isLoading: boolean = false;
  isNavOpen = true;
  userPhotoUrl = '';
  portalTitle = '';
  welcomeText = '';
  sysSecuredMenus: SysSecuredMenu[] = [];

  portalMode?: SysExternalWebUserType;
  SysExternalWebUserType = SysExternalWebUserType;

  userSecondsIdle?: number;
  idleTimeOutLimit = 6000;
  displayIdleConfirmDialog = false;

  showRefreshPopup = false;

  showLegalNotice: boolean = false;
  showContactUs = false;
  portalConfig!: ExternalPortalConfig;
  legalText: string | undefined;

  currentYear = new Date().getFullYear();

  private idleSubscription: Subscription = new Subscription();
  private subscription: Subscription = new Subscription();
  private jwtHelper = new JwtHelperService();

  constructor(
    private translateService: TranslateService,
    private localStorageService: LocalStorageService,
    private authService: AuthService,
    private userIdle: UserIdleService,
    private actionsService: ActionsService,
    private portalHttpService: PortalHttpService,
    private ref: ChangeDetectorRef,
    private readonly store: Store,
    private swUpdate: SwUpdate,
    private loadingService: HttpLoadingIndicatorService
  ) {
    setDefaultDevextremeOptions();
  }

  ngOnInit(): void {
    this.subscribeToSoftwareUpdate();
    this.subscribeToTokenLocalStorage();
    this.setDefaultLang();
    this._setDefaultBackgrounds();
    this._getExternalPortalConfig();

    this.localStorageService.setStorageItem(
      Identifier.TYPE_API_KEY,
      '372992E6-21C6-4A27-80C1-FF9688C91F3A'
    );

    this.store.select(selectPortalMode).subscribe(portalMode => {
      this.portalMode = portalMode;
      if (!this.portalMode) return;

      if (this.portalMode === SysExternalWebUserType.SSICSASFPortal) {
        this.portalTitle = `${this.translateService.instant('general.smartServices')} ${this.translateService.instant('general.division')}`;
        this.legalText = this.portalConfig?.asfPortalLegalText;
        this.sysSecuredMenus = [
          {
            id: 1000,
            parentMenuId: undefined,
            sysApplicationId: 1,
            nameEn: 'Search',
            sequenceNumber: 1,
            levelId: 1,
            isLeaf: true,
            contextualHelpId: undefined,
            isActive: true,
            children: [],
            icon: 'icon-search-c',
            route: 'search',
          },
        ];
      } else {
        this.legalText = this.portalConfig?.clientPortalLegalText;
        this.portalTitle = `${this.translateService.instant('general.smartServices')} ${this.translateService.instant('referenceTables.clientPortal')}`;
        this.sysSecuredMenus = [
          {
            id: 1000,
            parentMenuId: undefined,
            sysApplicationId: 1,
            nameEn: 'My Contracts',
            sequenceNumber: 1,
            levelId: 1,
            isLeaf: true,
            contextualHelpId: undefined,
            isActive: true,
            children: [],
            icon: 'icon-task-edit-c',
            route: 'mycontracts',
          },
          {
            id: 1001,
            parentMenuId: undefined,
            sysApplicationId: 1,
            nameEn: 'My Forms',
            sequenceNumber: 1,
            levelId: 1,
            isLeaf: true,
            contextualHelpId: undefined,
            isActive: true,
            children: [],
            icon: 'icon-search-c',
            route: 'search',
          },
        ];
      }
    });

    this.loadingService.loading$.subscribe(val => {
      this.isLoading = val;
      this.ref.detectChanges();
    });
  }

  private _setDefaultBackgrounds() {
    this.portalHttpService.getDefaultBackgroundImages().subscribe({
      next: images => {
        let main: string = '';
        let popup: string = '';
        images.forEach(img => {
          if (img.isMainBackgroundImage) main = img.fileUri;
          if (img.isPopupBackgroundImage) popup = img.fileUri;
        });

        this.actionsService.setMainImage(main ? main : '/../assets/images/bombardier-bg.jpg');
        this.actionsService.setPopupImage(popup ? popup : '/../assets/images/bombardier-bg2.jpg');
      },
    });
  }

  private _getExternalPortalConfig() {
    this.portalHttpService.getExternalPortalConfiguration().subscribe(res => {
      if (res) {
        this.portalConfig = res;
        this.ref.markForCheck();
      }
    });
  }

  setDefaultLang() {
    const curLang = 'en-CA';
    this.translateService.setDefaultLang(curLang);
    this.translateService.use(curLang);
    dxLocale.locale(curLang);
  }

  private subscribeToTokenLocalStorage(): void {
    this.subscription.add(
      this.localStorageService.getStorageItem(Identifier.TYPE_TOKEN).subscribe(token => {
        if (token) {
          this.subscribeAuthenticatedUserChanged();
          this.setIdleCheck(60, token); // TODO 60 minutes to be changed to what is configured for system
          if (token && !this.jwtHelper.isTokenExpired(token)) {
            this.isLoggedIn = true;
          } else {
            this.isLoggedIn = false;
          }
        } else {
          this.flushIdleTimer();
          this.isLoggedIn = false;
        }
      })
    );
  }

  private subscribeToSoftwareUpdate(): void {
    if (this.swUpdate.isEnabled) {
      this.subscription.add(
        this.swUpdate.versionUpdates
          .pipe(filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'))
          .subscribe(() => {
            this.showRefreshPopup = true;
            this.ref.markForCheck();
          })
      );
    }
  }

  private subscribeToTimerStart(): void {
    this.idleSubscription.add(
      this.userIdle.onTimerStart().subscribe(count => {
        if (count === 1) {
          this.store.dispatch(setUserIdle({ isUserIdle: true }));
        }
        this.userSecondsIdle = count;
        console.log(this.userSecondsIdle);
        this.ref.markForCheck();
      })
    );
  }

  private subscribeToTimerTimeOut(): void {
    this.idleSubscription.add(
      this.userIdle.onTimeout().subscribe(() => {
        if (this.displayIdleConfirmDialog) {
          this.displayIdleConfirmDialog = false;
        }
        this.endIdleTimerAndSession();
      })
    );
  }

  private startWatching(): void {
    this.userIdle.startWatching();
  }

  private showConfirmDialog(): void {
    this.displayIdleConfirmDialog = true;
    this.ref.markForCheck();
  }

  private flushIdleTimer(): void {
    this.userIdle.stopWatching();
    this.displayIdleConfirmDialog = false;
    this.idleSubscription.unsubscribe();
    this.idleSubscription = new Subscription();
    this.ref.markForCheck();
  }

  private setIdleCheck(inactivityInMinutes: number, token: string): void {
    this.flushIdleTimer();
    this.userIdle.setConfigValues({
      idle: inactivityInMinutes * 60,
      timeout: this.idleTimeOutLimit,
      ping: 120,
    });
    if (token && !this.jwtHelper.isTokenExpired(token)) {
      this.startWatching();
      this.idleSubscription.add(
        this.store
          .select(selectUserIdle)
          .pipe(filter((idle: boolean | undefined) => !!idle))
          .subscribe(() => this.showConfirmDialog())
      );
      this.subscribeToTimerStart();
      this.subscribeToTimerTimeOut();
    } else {
      this.store.dispatch(setUserIdle({ isUserIdle: false }));
    }
    this.ref.markForCheck();
  }

  restartIdleTimer(): void {
    this.userIdle.stopWatching();
    this.displayIdleConfirmDialog = false;
    this.userIdle.startWatching();
    this.store.dispatch(setUserIdle({ isUserIdle: false }));
    this.ref.markForCheck();
  }

  endIdleTimerAndSession(): void {
    this.flushIdleTimer();
    this.store.dispatch(setUserIdle({ isUserIdle: false }));
    // this.dynamicLoaderService.removeAllDynamicComponents(); // might not need it , kept it for debuggin/testing
    this.authService.logout();
    this.ref.markForCheck();
  }

  getUserShortName(): string | undefined {
    if (this.portalUser && this.portalUser.fullName) {
      return (
        this.portalUser.fullName
          .split(' ')
          .map((n: string) => n[0])
          .join('') || 'DevAdmin'
      );
    }
    return undefined;
  }

  subscribeAuthenticatedUserChanged() {
    this.subscription.add(
      this.authService.userAuthenticationChanged$?.subscribe(user => {
        if (user) {
          if (user.sysExternalWebUserTypeId) {
            this.store.dispatch(setPortalMode({ portalMode: user.sysExternalWebUserTypeId }));
          }
          this.portalUser = user;
          this.welcomeText = `${this.translateService.instant('general.welcome')} ${user.fullName ? user.fullName : ''} ${user.companyName && user.fullName ? 'of ' : ''}${user.companyName || ''}`;
          this.isLoggedIn = true;
        } else {
          this.isLoggedIn = false;
        }
        this.ref.markForCheck();
      })
    );
  }

  navToggle(): void {
    this.isNavOpen = !this.isNavOpen;
    this.ref.markForCheck();
  }

  onSignOutClick(): void {
    this.authService.logout();
    this.ref.markForCheck();
  }

  onRouteActivate(): void {
    this.isNavOpen = false;
    this.ref.markForCheck();
  }

  closeEditMode() {
    this.showLegalNotice = false;
    this.showContactUs = false;
    this.ref.markForCheck();
  }

  onlegalNoticePopupClick(): void {
    this.showLegalNotice = true;
    this.ref.markForCheck();
  }

  onContactUsClick(): void {
    this.showContactUs = true;
    this.ref.markForCheck();
  }

  updateToLatest(): void {
    this.swUpdate.activateUpdate().then(() => document.location.reload());
  }
}
